import { useFormikContext } from 'formik';
import { Space, Typography } from 'antd';
import { useCallback, useRef, useState } from 'react';
import Button from 'components/new-ui/base/button/Button';
import ProfilePicture, { PictureState, ProfilePictureRef } from 'components/ui/composed/imageUpload/ProfilePicture';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import CountrySelect from 'components/new-ui/composed/selects/CountrySelect';
import FormEori from 'components/ui/composed/formEori/FormEori';
import { invalidEoriMessage } from 'views/declarations/utils/validation-utils';
import { FormCol, FormRow, FieldLabel } from '../../Utils';
import { FormFields, FormValues } from '../../types';

type Props = {
    profilePicture: string | null;
    handleProfilePictureUpload: (file: File) => Promise<void>;
    handleProfilePictureDelete: () => Promise<void>;
};

const PartyDetailsForm = ({ profilePicture, handleProfilePictureUpload, handleProfilePictureDelete }: Props) => {
    const formik = useFormikContext<FormValues>();

    const profilePicRef = useRef<ProfilePictureRef | null>(null);
    const [pictureState, setPictureState] = useState<PictureState>({
        uploading: false,
        deleting: false,
    });

    const onEoriError = useCallback(() => {
        formik.setFieldError(FormFields.CustomerDetails.EORI, invalidEoriMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onEoriSuccess = useCallback(
        () => {
            formik.setFieldError(FormFields.CustomerDetails.EORI, undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <ProfilePicture
                    size={80}
                    ref={profilePicRef}
                    profilePicture={profilePicture}
                    onUpload={handleProfilePictureUpload}
                    onDelete={handleProfilePictureDelete}
                    onPictureStateChange={setPictureState}
                />
                <Space size={4} direction="vertical">
                    <Space size={16}>
                        <Button
                            loading={pictureState.uploading}
                            onClick={() => profilePicRef.current?.openFileDialog()}
                            style={{ color: 'var(--brand-6)', borderColor: 'inherit' }}
                        >
                            Upload new image
                        </Button>
                        <Button
                            loading={pictureState.deleting}
                            onClick={handleProfilePictureDelete}
                            style={{ color: 'var(--dust-red-5)', borderColor: 'inherit' }}
                        >
                            Delete
                        </Button>
                    </Space>
                    <Typography.Text style={{ color: 'var(--gray-7)' }}>
                        Recommended 80x80px in PNG or JPEG format
                    </Typography.Text>
                </Space>
            </div>

            <Space size={12} direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                <FormRow>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.CUSTOMER_NAME} required>
                            Party Name
                        </FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter Party Name"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.CUSTOMER_NAME)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.CUSTOMER_NAME)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.CUSTOMER_NAME)}
                            />
                        </div>
                    </FormCol>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.REFERENCE_CODE} required>
                            Reference Code
                        </FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter Reference Code"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.REFERENCE_CODE)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.REFERENCE_CODE)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.REFERENCE_CODE)}
                            />
                        </div>
                    </FormCol>
                </FormRow>

                <FormRow>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.EORI} required>
                            EORI
                        </FieldLabel>
                        <div className="fixed-row-layout">
                            <FormEori
                                condensed
                                label={undefined}
                                placeholder="Enter EORI number"
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.EORI)}
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.EORI)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.EORI)}
                                onError={onEoriError}
                                onSuccess={onEoriSuccess}
                            />
                        </div>
                    </FormCol>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.VAT_NUMBER}>VAT Number</FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter VAT Number"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.VAT_NUMBER)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.VAT_NUMBER)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.VAT_NUMBER)}
                            />
                        </div>
                    </FormCol>
                </FormRow>

                <FormRow>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.DEFERRED_ACCOUNT_NUMBER}>
                            Deferred Account Number
                        </FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter Deferred Account Number"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.DEFERRED_ACCOUNT_NUMBER)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.DEFERRED_ACCOUNT_NUMBER)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.DEFERRED_ACCOUNT_NUMBER)}
                            />
                        </div>
                    </FormCol>
                </FormRow>

                <FormRow>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.ADDRESS_LINE_1} required>
                            Address Line 1
                        </FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter Address Line 1"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.ADDRESS_LINE_1)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.ADDRESS_LINE_1)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.ADDRESS_LINE_1)}
                            />
                        </div>
                    </FormCol>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.ADDRESS_LINE_2}>Address Line 2</FieldLabel>
                        <div>
                            <FormInput
                                inputStyles={{ maxWidth: '100%' }}
                                placeholder="Enter Address Line 1"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.ADDRESS_LINE_2)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.ADDRESS_LINE_2)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.ADDRESS_LINE_2)}
                            />
                        </div>
                    </FormCol>
                </FormRow>

                <FormRow>
                    <FormCol>
                        <FieldLabel htmlFor={FormFields.CustomerDetails.COUNTRY} required>
                            Country
                        </FieldLabel>
                        <div>
                            <CountrySelect
                                hideLabel
                                style={{ maxWidth: '100%' }}
                                placeholder="Select Country"
                                fieldProps={formik.getFieldProps(FormFields.CustomerDetails.COUNTRY)}
                                fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.COUNTRY)}
                                fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.COUNTRY)}
                            />
                        </div>
                    </FormCol>
                    <FormCol>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div>
                                <FieldLabel htmlFor={FormFields.CustomerDetails.POST_CODE} required>
                                    Post Code
                                </FieldLabel>
                                <FormInput
                                    placeholder="Code"
                                    fieldProps={formik.getFieldProps(FormFields.CustomerDetails.POST_CODE)}
                                    fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.POST_CODE)}
                                    fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.POST_CODE)}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <FieldLabel htmlFor={FormFields.CustomerDetails.CITY} required>
                                    City
                                </FieldLabel>
                                <div>
                                    <FormInput
                                        inputStyles={{ maxWidth: '100%' }}
                                        placeholder="Enter City"
                                        fieldProps={formik.getFieldProps(FormFields.CustomerDetails.CITY)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.CustomerDetails.CITY)}
                                        fieldMeta={formik.getFieldMeta(FormFields.CustomerDetails.CITY)}
                                    />
                                </div>
                            </div>
                        </div>
                    </FormCol>
                </FormRow>
            </Space>
        </div>
    );
};

export default PartyDetailsForm;
